import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';

const MusicalSpecialtiesStep = ({ formData, setFormData, errors }) => {
  const musicalGenres = [
    { value: 'electronic', label: 'Électronique' },
    { value: 'house', label: 'House' },
    { value: 'techno', label: 'Techno' },
    { value: 'trance', label: 'Trance' },
    { value: 'dubstep', label: 'Dubstep' },
    { value: 'drum_bass', label: 'Drum & Bass' },
    { value: 'hip_hop', label: 'Hip-Hop' },
    { value: 'rock', label: 'Rock' },
    { value: 'pop', label: 'Pop' },
    { value: 'jazz', label: 'Jazz' },
    { value: 'classical', label: 'Classique' },
    { value: 'reggae', label: 'Reggae' },
    { value: 'latin', label: 'Latin' },
    { value: 'world', label: 'Musique du Monde' },
    { value: 'other', label: 'Autre' }
  ];

  const experienceLevels = [
    { value: 'beginner', label: 'Débutant (0-2 ans)' },
    { value: 'intermediate', label: 'Intermédiaire (2-5 ans)' },
    { value: 'experienced', label: 'Expérimenté (5-10 ans)' },
    { value: 'expert', label: 'Expert (10+ ans)' }
  ];

  const venueTypes = [
    { value: 'nightclub', label: 'Boîtes de nuit' },
    { value: 'festival', label: 'Festivals' },
    { value: 'bar_restaurant', label: 'Bars/Restaurants' },
    { value: 'concert_hall', label: 'Salles de concert' },
    { value: 'private_events', label: 'Événements privés' },
    { value: 'corporate', label: 'Événements d\'entreprise' },
    { value: 'wedding', label: 'Mariages' },
    { value: 'outdoor', label: 'Événements en plein air' }
  ];

  const equipmentTypes = [
    { value: 'dj_controller', label: 'Contrôleur DJ' },
    { value: 'turntables', label: 'Platines vinyles' },
    { value: 'cdj', label: 'Lecteurs CDJ' },
    { value: 'mixer', label: 'Table de mixage' },
    { value: 'speakers', label: 'Enceintes' },
    { value: 'microphones', label: 'Microphones' },
    { value: 'lighting', label: 'Éclairage' },
    { value: 'instruments', label: 'Instruments live' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field, value, checked) => {
    setFormData(prev => {
      const currentValues = prev?.[field] || [];
      if (checked) {
        return {
          ...prev,
          [field]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [field]: currentValues?.filter(item => item !== value)
        };
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Spécialités Musicales
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Genres musicaux de spécialité *
            </label>
            <CheckboxGroup error={errors?.musicalGenres}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {musicalGenres?.map((genre) => (
                  <Checkbox
                    key={genre?.value}
                    label={genre?.label}
                    checked={(formData?.musicalGenres || [])?.includes(genre?.value)}
                    onChange={(e) => handleMultiSelectChange('musicalGenres', genre?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </CheckboxGroup>
            {errors?.musicalGenres && (
              <p className="text-sm text-error mt-1">{errors?.musicalGenres}</p>
            )}
          </div>
          
          <Select
            label="Niveau d'expérience *"
            placeholder="Sélectionnez votre niveau"
            options={experienceLevels}
            value={formData?.experienceLevel || ''}
            onChange={(value) => handleInputChange('experienceLevel', value)}
            error={errors?.experienceLevel}
            required
            description="Votre expérience dans l'industrie musicale"
          />
        </div>
      </div>
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Types de Lieux et Événements
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Types de lieux avec lesquels vous travaillez *
          </label>
          <CheckboxGroup error={errors?.venueTypes}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {venueTypes?.map((venue) => (
                <Checkbox
                  key={venue?.value}
                  label={venue?.label}
                  checked={(formData?.venueTypes || [])?.includes(venue?.value)}
                  onChange={(e) => handleMultiSelectChange('venueTypes', venue?.value, e?.target?.checked)}
                />
              ))}
            </div>
          </CheckboxGroup>
          {errors?.venueTypes && (
            <p className="text-sm text-error mt-1">{errors?.venueTypes}</p>
          )}
        </div>
      </div>
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Équipement et Matériel
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Équipement disponible
            </label>
            <CheckboxGroup>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {equipmentTypes?.map((equipment) => (
                  <Checkbox
                    key={equipment?.value}
                    label={equipment?.label}
                    checked={(formData?.equipment || [])?.includes(equipment?.value)}
                    onChange={(e) => handleMultiSelectChange('equipment', equipment?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </CheckboxGroup>
          </div>
          
          <Input
            label="Détails sur l'équipement"
            type="text"
            placeholder="Marques, modèles spécifiques, équipement spécialisé..."
            value={formData?.equipmentDetails || ''}
            onChange={(e) => handleInputChange('equipmentDetails', e?.target?.value)}
            description="Informations supplémentaires sur votre matériel"
          />
        </div>
      </div>
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Expérience et Références
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Années d'expérience *"
            type="number"
            placeholder="5"
            min="0"
            max="50"
            value={formData?.yearsExperience || ''}
            onChange={(e) => handleInputChange('yearsExperience', e?.target?.value)}
            error={errors?.yearsExperience}
            required
            description="Nombre d'années dans l'industrie musicale"
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Artistes ou événements notables
            </label>
            <textarea
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              rows="4"
              placeholder="Décrivez les artistes avec lesquels vous avez travaillé ou les événements que vous avez organisés..."
              value={formData?.notableWork || ''}
              onChange={(e) => handleInputChange('notableWork', e?.target?.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Partagez vos expériences les plus marquantes
            </p>
          </div>
          
          <Input
            label="Capacité d'audience moyenne"
            type="number"
            placeholder="500"
            min="0"
            value={formData?.averageAudience || ''}
            onChange={(e) => handleInputChange('averageAudience', e?.target?.value)}
            description="Nombre moyen de personnes dans vos événements"
          />
        </div>
      </div>
    </div>
  );
};

export default MusicalSpecialtiesStep;